import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CommunityDetails from '../components/CommunityDetails';
import DemoHeader from '../components/Demo/DemoHeader';
import DemoInput from '../components/Demo/DemoInput';
import './Demo.css';
import './Messenger.css';
import { getVisitorId } from '../utils/abTest';
import TypingIndicator from '../components/Message/TypingIndicator';
import '../components/AIOnboarding/AIOnboarding.css';
import { renderMessageText, chunkMs, typeDuration } from '../components/AIOnboarding/chunkText';

// After the example people: the offer to suggest real people. Said only when
// the community really has suitable members (GET /aiOnboarding/has-people).
const offerText = (matches) => `${matches ? 'We **have some people like this**, and there are' : 'There are'} **more joining** who will likely fit in the future. Would you like me to **suggest real people** and **let you know when others who might fit join**?`;
const YES = /^\s*(y|yes|yeah|yep|yup|sure|ok|okay|please|yes please|go on|definitely|of course|i would|i'd like)\b/i;
const NO = /^\s*(n|no|nope|not now|not yet|maybe later|later|no thanks)\b/i;

const Demo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // The offer after the example people (offerText) — waiting for yes / not now.
  const [awaitingOffer, setAwaitingOffer] = useState(false);
  const demoMatchesRef = useRef(false);
  // The demo opens with /meet's first question (GET /aiOnboarding/first-question)
  // and its tappable options; their answer — tapped or typed — is what the
  // example people are based on, and goes on to /meet (startFromDemo) so it
  // isn't asked again.
  const [firstQ, setFirstQ] = useState(null);
  const [awaitingFirst, setAwaitingFirst] = useState(false);
  const [firstPicked, setFirstPicked] = useState([]);
  const demoAnswerRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [awaitingDemoGate, setAwaitingDemoGate] = useState(false);
  // "Start Demo" stays put and its label crossfades to "Demo Active"; only
  // "Sign Up Now" fades away and unmounts. demoTransitioning is just the
  // brief window while both of those animations are running.
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoTransitioning, setDemoTransitioning] = useState(false);
  // Tracks which suggested profiles the user has clicked "Interested in
  // connecting" on, purely for the button's visual click-state — the click
  // itself still routes into the same signup prompt as before.
  const [interestedPeople, setInterestedPeople] = useState(new Set());
  const messagesEndRef = useRef(null);
  const followUpTimeoutRef = useRef(null); // Track follow-up timeout to prevent duplicates

  // Check if we're in community-specific mode
  const urlParams = new URLSearchParams(location.search);
  const communityId = urlParams.get('community');
  const isCommunityMode = !!communityId;

  // Generate a new session ID
  const generateSessionId = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  // Auto-scroll to bottom of messages
  // Whether the chosen community has suitable members (the offer's wording).
  const selectedRef = selectedCommunity?.databaseRef;
  useEffect(() => {
    if (!selectedRef) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/aiOnboarding/has-people/${encodeURIComponent(selectedRef)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { demoMatchesRef.current = !!d?.matches; })
      .catch(() => {});
  }, [selectedRef]);

  useEffect(() => {
    if (!selectedRef) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/aiOnboarding/first-question/${encodeURIComponent(selectedRef)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.question) setFirstQ(d); })
      .catch(() => {});
  }, [selectedRef]);

  // Their answer to the first question from the options: on to the bot.
  const sendFirstAnswer = (values) => {
    if (!values.length || !firstQ) return;
    demoAnswerRef.current = { key: firstQ.key, values };
    setAwaitingFirst(false);
    setFirstPicked([]);
    handleSendMessage(values.join(', '), { answeredFirst: true });
  };

  const showOffer = () => {
    setMessages(prev => [...prev, { id: Date.now() + 5, sender: 'bot', text: offerText(demoMatchesRef.current), timestamp: new Date(), offerReal: true }]);
    setAwaitingOffer(true);
  };

  // Yes: carry on in /meet — the demo conversation comes across, then the
  // questions (backend startFromDemo). Not now: the usual sign-up button.
  const handleOffer = (choice, fromButton = true) => {
    if (fromButton) {
      setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text: choice === 'yes' ? 'Yes please' : 'Not now', timestamp: new Date() }]);
    }
    setAwaitingOffer(false);
    const communityId = selectedCommunity?._id || selectedCommunity?.communityId;
    if (choice === 'yes' && selectedRef) {
      // Their first answer goes with them (read by /meet's start).
      try { sessionStorage.setItem('demoFirstAnswer', JSON.stringify(demoAnswerRef.current || null)); } catch { /* ignore */ }
      navigate(`/meet/${encodeURIComponent(selectedRef)}?demo=${encodeURIComponent(sessionId || '')}`);
      return;
    }
    setMessages(prev => [...prev, {
      id: Date.now() + 1,
      sender: 'bot',
      text: "No problem — you can **sign up** whenever you like. It's **free** and takes about 3 minutes.",
      timestamp: new Date(),
      showSignupButton: true,
      communityId,
    }]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Trigger auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Fetch communities on mount
  useEffect(() => {
    // Retried a few times — one failed request (a blip, a server restart)
    // used to leave the demo stuck on "Loading community…".
    let cancelled = false;
    const fetchCommunities = async (attempt = 1) => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled && Array.isArray(data)) setCommunities(data);
      } catch (error) {
        console.error('Error fetching communities:', error);
        if (!cancelled && attempt < 5) setTimeout(() => fetchCommunities(attempt + 1), 1500 * attempt);
      }
    };
    fetchCommunities();
    return () => { cancelled = true; };
  }, []);

  // Initialize conversation with improved initial message
  const initializeConversation = async (community, newSessionId) => {
    const initialMessage = community ?
      `Hi! This is a  **demo** of how joining ${community.title} would work. To get suggested actual people from the community, you'll need to sign up below.` :
      `Hi there! What communities, activities, or types of people are you interested in connecting with?`;

    // Send initial message to backend so AI is aware of it
    if (community) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/demoCommunityAI/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: "INIT_SESSION", // Special message to initialize session with bot message
            sessionId: newSessionId,
            initialBotMessage: initialMessage,
            communityId: community._id || community.communityId,
            mode: 'community',
            visitorId: getVisitorId()
          })
        });
      } catch (error) {
        console.error('Error initializing session:', error);
      }
    }

    setAwaitingDemoGate(!!community);

    const msgs = [{
      id: 1,
      sender: 'bot',
      text: initialMessage,
      timestamp: new Date(),
      showDemoGateButtons: !!community,
      communityId: community ? (community._id || community.communityId) : null
    }];
    setMessages(msgs);
  };

  // Initialize conversation based on mode
  useEffect(() => {
    // Generate new session ID for each conversation mode change
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    
    if (isCommunityMode && communityId) {
      if (communities.length === 0) {
        // Show loading message while waiting for communities to load
        setMessages([{
          id: 1,
          sender: 'bot',
          text: 'Loading community...',
          timestamp: new Date()
        }]);
        return; // Don't initialize yet, but continue rendering
      } else {
        // Community-specific mode - communities are loaded
        const community = communities.find(c => c._id === communityId || c.communityId === communityId);
        if (community?.databaseRef) {
          // A community's demo is /meet in demo mode (its questions, then
          // people like them and the offer) — older links land there.
          navigate(`/meet/${encodeURIComponent(community.databaseRef)}?mode=demo`, { replace: true });
        } else if (community) {
          setSelectedCommunity(community);
          initializeConversation(community, newSessionId);
        } else {
          // Not a group we can show (hidden, archived or a wrong link): the
          // general demo instead of "Loading community…" for ever.
          initializeConversation(null, newSessionId);
        }
      }
    } else if (!isCommunityMode) {
      // General platform demo mode
      initializeConversation(null, newSessionId);
    }
  }, [isCommunityMode, communityId, communities]);

  const handleSendMessage = async (messageText, { answeredFirst = false } = {}) => {
    if (!messageText?.trim()) return;

    // Ensure we have a session ID
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      setSessionId(currentSessionId);
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Answering the offer by typing: yes / not now; anything else goes to the bot.
    if (awaitingOffer && (YES.test(messageText) || NO.test(messageText))) {
      handleOffer(YES.test(messageText) ? 'yes' : 'no', false);
      return;
    }

    // Typed straight in without tapping Start Demo: "sign up" goes to the
    // sign-up page; anything else starts the demo with what they said as
    // their answer to the first question (many start by saying what they're
    // into) — it goes to the bot rather than being dropped.
    if (awaitingDemoGate) {
      const lower = messageText.toLowerCase();
      if (lower.includes('sign up') || lower.includes('signup')) {
        handleDemoGateChoice('signup', selectedCommunity?._id || selectedCommunity?.communityId);
        return;
      }
      setAwaitingDemoGate(false);
      setDemoStarted(true);
      if (firstQ) demoAnswerRef.current = { key: firstQ.key, text: messageText };
    } else if (awaitingFirst && !answeredFirst) {
      // Typed their answer to the first question instead of tapping.
      setAwaitingFirst(false);
      if (firstQ) demoAnswerRef.current = { key: firstQ.key, text: messageText };
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demoCommunityAI/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          sessionId: currentSessionId,
          communityId: selectedCommunity?._id || selectedCommunity?.communityId,
          mode: isCommunityMode ? 'community' : 'general',
          visitorId: getVisitorId(),
          communities: communities.map(c => ({ 
            id: c._id, 
            title: c.title, 
            type: c.type, 
            description: c.mainDescription,
            mainDescription: c.mainDescription,
            location: c.location,
            tags: c.tags,
            searchKeywords: c.searchKeywords
          }))
        })
      });

      const data = await response.json();
      
      // Update sessionId if backend provided a new one
      if (data.sessionId && data.sessionId !== currentSessionId) {
        setSessionId(data.sessionId);
      }
      
      // Parse AI response for profile suggestions and community IDs
      const { profiles, cleanedText } = parseAIProfileSuggestions(data.response);
      const { specificCommunityIds, followUpQuery, finalText } = parseSpecificCommunities(cleanedText);
      
      // Debug: Log the parsing results
      console.log('Frontend debug:', {
        action: data.action,
        originalResponse: data.response,
        specificCommunityIds,
        followUpQuery,
        finalText
      });
      
      const botMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        text: finalText, // Use cleaned text without community tags
        timestamp: new Date(),
        profiles: profiles.length > 0 ? profiles : null, // Include profiles in the message
        specificCommunityIds: specificCommunityIds.length > 0 ? specificCommunityIds : null
      };

      // Handle special actions based on AI response BEFORE adding to messages
      if (data.action === 'show_communities') {
        // Show all communities in this message
        botMessage.showCommunityCards = true;
        botMessage.communityCardsType = 'default';
        botMessage.specificCommunityIds = [];
      } else if (data.action === 'show_specific_communities' && botMessage.specificCommunityIds) {
        // Show only the specific communities mentioned by the AI
        console.log('Setting showCommunityCards=true with IDs:', botMessage.specificCommunityIds);
        botMessage.showCommunityCards = true;
        botMessage.communityCardsType = 'default';
        // specificCommunityIds already set from parsing
      } else if (data.action === 'suggest_alternatives') {
        // Show a selection of alternative communities
        botMessage.showCommunityCards = true;
        botMessage.communityCardsType = 'alternatives';
        botMessage.specificCommunityIds = [];
      } else if (data.action === 'create_community') {
        // Add community creation button to the bot message
        botMessage.showCreateCommunityButton = true;
      } else if (data.action === 'connect_me') {
        // "Connect me": the offer to suggest real people (handleOffer).
        botMessage.text = offerText(demoMatchesRef.current);
        botMessage.offerReal = true;
        setAwaitingOffer(true);
      } else if (data.action === 'show_people' && profiles.length > 0) {
        // Once the example people are in: the offer to suggest real people.
        setTimeout(showOffer, typeDuration(finalText) + 900);
      }
      
      // Add the message to state AFTER all properties are set
      setMessages(prev => [...prev, botMessage]);

      // Send follow-up message after community cards for specific communities
      if (data.action === 'show_specific_communities' && followUpQuery) {
        // Clear any existing timeout to prevent duplicates
        if (followUpTimeoutRef.current) {
          clearTimeout(followUpTimeoutRef.current);
        }
        
        followUpTimeoutRef.current = setTimeout(() => {
          const followUpMessage = {
            id: Date.now() + 2,
            sender: 'bot',
            text: `Do you want to join any of these groups?\n\nRefine your search?\n\nOr perhaps we could create a group that better fits your interests and get others to join?\n\nWhat would you like to do next?`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, followUpMessage]);
          followUpTimeoutRef.current = null; // Clear the ref after execution
        }, 1000); // 1 second delay to let community cards load
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        text: "Sorry, I'm having trouble connecting right now. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleCommunitySelect = (community) => {
    setSelectedCommunity(community);
    
    const botMessage = {
      id: Date.now(),
      sender: 'bot',
      text: `Great choice! ${community.title} is a fantastic community. What sort of people would you like to connect with in this group?`,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, botMessage]);
  };

  const parseAIProfileSuggestions = (aiResponse) => {
    // Look for the **Suggestions:** section in the AI response
    const suggestionsMatch = aiResponse.match(/\*\*Suggestions:\*\*([\s\S]*?)(?=\n\n|\n$|$)/);
    
    if (!suggestionsMatch) return { profiles: [], cleanedText: aiResponse };
    
    const suggestionsText = suggestionsMatch[1];
    const profiles = [];
    
    // Parse each profile suggestion using the format:
    // • **Name:** [Name] **Age:** [Age] **Gender:** [Gender] - [Bio]
    const profileRegex = /•\s*\*\*Name:\*\*\s*([^*]+?)\s*\*\*Age:\*\*\s*([^*]+?)\s*\*\*Gender:\*\*\s*([^-]+?)\s*-\s*(.+?)(?=\n•|\n$|$)/gs;
    
    let match;
    let profileId = 1;
    
    while ((match = profileRegex.exec(suggestionsText)) !== null) {
      const [, name, age, gender, bio] = match;
      
      profiles.push({
        id: profileId++,
        name: name.trim(),
        age: age.trim(),
        gender: gender.trim(),
        bio: bio.trim(),
        avatar: null, // Will use CSS-generated avatar
        community: selectedCommunity?.title || 'Community'
      });
    }
    
    // Remove the suggestions section from the AI response text
    const cleanedText = aiResponse.replace(/\*\*Suggestions:\*\*[\s\S]*$/, '').trim();
    
    return { profiles, cleanedText };
  };

  const parseSpecificCommunities = (text) => {
    // Look for the [SHOW_COMMUNITIES:id1,id2] tag in the text
    const communityMatch = text.match(/\[SHOW_COMMUNITIES:([^\]]+)\]/);
    // Look for the [FOLLOW_UP_QUERY:query] tag in the text
    const followUpMatch = text.match(/\[FOLLOW_UP_QUERY:([^\]]+)\]/);
    
    if (!communityMatch) return { specificCommunityIds: [], followUpQuery: null, finalText: text };
    
    const communityIds = communityMatch[1].split(',').map(id => id.trim());
    const followUpQuery = followUpMatch ? followUpMatch[1] : null;
    
    // Remove both tags from the text
    let finalText = text.replace(/\[SHOW_COMMUNITIES:[^\]]+\]/, '').trim();
    finalText = finalText.replace(/\[FOLLOW_UP_QUERY:[^\]]+\]/, '').trim();
    
    // AGGRESSIVE: Remove any follow-up text that the AI might have generated
    // Remove common follow-up patterns
    finalText = finalText.replace(/Do you want to join any of these groups\?[\s\S]*$/i, '').trim();
    finalText = finalText.replace(/Would you like to join[\s\S]*$/i, '').trim();
    finalText = finalText.replace(/Refine your search\?[\s\S]*$/i, '').trim();
    finalText = finalText.replace(/Or perhaps we could create[\s\S]*$/i, '').trim();
    
    return { specificCommunityIds: communityIds, followUpQuery, finalText };
  };

  const handlePersonClick = () => {
    // Tapping an example person: the offer to suggest real people.
    if (!awaitingOffer) showOffer();
  };


  // Clicking "Interested in connecting" on a suggested profile is distinct
  // from clicking the card itself (handlePersonClick above) — it gets its
  // own explanation of what happens next instead of the generic signup nudge.
  // Deliberately doesn't name the specific profile — this is about how the
  // real feature works in general, not a promise tied to one simulated person.
  const handleInterestedClick = () => {
    const communityId = selectedCommunity?._id || selectedCommunity?.communityId;
    const reasons = selectedCommunity?.connectionReasons || [];
    // Ideally the AI would personalize this using both the community's
    // purpose and what the visitor actually asked for earlier in the chat —
    // for now it combines the community's general connection reasons with
    // the visitor's own most recent chat message as a lightweight stand-in.
    const purpose = reasons.length > 0
      ? reasons[Math.floor(Math.random() * reasons.length)].toLowerCase()
      : null;
    const lastUserMessage = [...messages].reverse().find(m => m.sender === 'user')?.text;

    let suggestion = '';
    if (purpose && lastUserMessage) {
      suggestion = ` You could then perhaps plan something around ${purpose}, or what you mentioned earlier — "${lastUserMessage}".`;
    } else if (purpose) {
      suggestion = ` You could then perhaps plan something around ${purpose}.`;
    } else if (lastUserMessage) {
      suggestion = ` You could then perhaps plan something around what you mentioned earlier — "${lastUserMessage}".`;
    }

    const interestMessage = `Thanks! Once you're signed up, whenever you show you're interested in someone in the community, I'll let them know — and if you both agree, I'll put you in touch over Messenger.${suggestion}`;

    const botMessage = {
      id: Date.now(),
      sender: 'bot',
      text: interestMessage,
      timestamp: new Date(),
      showSignupButton: true,
      communityId: communityId
    };

    setMessages(prev => [...prev, botMessage]);
  };

  // Sign up always happens on the real signup page — never embedded in the
  // chat itself. Opens in a new tab, same as clicking a plain link would.
  // `from`: 'first_message' (the Sign Up button on the opening message,
  // before any demo) or 'chat' (the Join button later in the demo).
  const handleSignupClick = (communityId, from = 'chat') => {
    if (sessionId) {
      // Fire-and-forget: only an actual click on Sign Up should mark the
      // demo session as having reached signup — not anything typed in chat.
      // keepalive so the request still goes out when opening the signup
      // page replaces this one (Instagram/Facebook in-app browsers open
      // '_blank' links in the same view, which cancels ordinary requests).
      fetch(`${process.env.REACT_APP_API_BASE_URL}/demoCommunityAI/reached-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, from }),
        keepalive: true,
      }).catch(() => {});
    }
    window.open(`${window.location.origin}/groupsearch/join/${communityId}`, '_blank', 'noopener,noreferrer');
  };

  // Resolve the "Okay to demo? Or sign up now?" gate shown with the intro message.
  const handleDemoGateChoice = (choice, communityIdForGate) => {
    if (choice === 'signup') {
      setAwaitingDemoGate(false);
      handleSignupClick(communityIdForGate, 'first_message');
      return;
    }

    // "Sign Up Now" fades away; "Start Demo" stays put and its label
    // crossfades to "Demo Active". Then a typing indicator, then the
    // actual demo-start message — a beat of transition, not an instant jump.
    setDemoTransitioning(true);

    setTimeout(() => {
      setAwaitingDemoGate(false);
      setDemoStarted(true);
      setDemoTransitioning(false);

      const communityTitle = selectedCommunity?.title || 'the community';
      // /meet's first question, with its options (else the general one).
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: 'bot',
        text: firstQ ? `Great! ${firstQ.question}` : `Great! What sort of people are you looking to meet in **${communityTitle}**?`,
        timestamp: new Date(),
        firstQuestion: !!firstQ,
      }]);
      if (firstQ) setAwaitingFirst(true);
    }, 400);
  };

  // Same rule as signup: always the real page, never embedded in the chat.
  const handleCreateCommunityClick = () => {
    window.open(`${window.location.origin}/create`, '_blank', 'noopener,noreferrer');
  };

  // Render **bold** segments within a line of chat text.
  const renderBold = (text) => {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="demo-container aio-page">
      {/* Demo Chat Panel */}
      <div className="demo-chatbox-panel demo-panel-active">
        <div className="demo-chatbox-wrapper">
          <div className="demo-chatbox-component">
            {/* Demo Header */}
            <div className="demo-chatbox-header">
              <DemoHeader 
                title={isCommunityMode 
                  ? `${selectedCommunity?.title || 'Community'} AI Bot`
                  : 'Platform Demo Chat'
                }
                image={selectedCommunity?.pics?.[0]}
                selectedCommunity={selectedCommunity}
              />
            </div>
            
            {/* Demo Messages */}
            <div className="demo-chatbox-messages">
              <div className="demo-messages">
            {messages.map((message) => (
              <React.Fragment key={message.id}>
                <div className={`demo-message ${message.sender}`}>
                  <div className="demo-message-content">
                    <div className="demo-message-text">
                      {/* The bot's words arrive a few at a time, like /meet. */}
                      {message.sender === 'bot' ? renderMessageText(message.text, { step: chunkMs(message.text) }) : message.text.split('\n\n').map((paragraph, index) => {
                        return (
                          <p key={index} className="demo-paragraph">
                            {paragraph.split('\n').map((line, lineIndex) => (
                              <span key={lineIndex}>
                                {renderBold(line)}
                                {lineIndex < paragraph.split('\n').length - 1 && <br />}
                              </span>
                            ))}
                          </p>
                        );
                      })}
                    </div>
                    <span className="demo-timestamp">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {/* Show signup button if this message has one */}
                    {message.showSignupButton && (
                      <div className="demo-signup-button-container">
                        <button 
                          className="demo-signup-button"
                          onClick={() => handleSignupClick(message.communityId)}
                        >
                          Join the Community
                        </button>
                      </div>
                    )}
                    {/* The first question's options: tap one, or several then Done. */}
                    {message.firstQuestion && awaitingFirst && firstQ?.options?.length > 0 && (
                      <div className="aio-choices">
                        {firstQ.options.map(option => {
                          const on = firstPicked.includes(option);
                          return (
                            <button
                              key={option}
                              type="button"
                              className={`aio-choice${on ? ' aio-choice--on' : ''}`}
                              onClick={() => (firstQ.multi
                                ? setFirstPicked(prev => (on ? prev.filter(o => o !== option) : [...prev, option]))
                                : sendFirstAnswer([option]))}
                            >
                              {firstQ.multi && on ? '✓ ' : ''}{option}
                            </button>
                          );
                        })}
                        {firstQ.multi && (
                          <button type="button" className="aio-choice aio-choice--done" disabled={!firstPicked.length} onClick={() => sendFirstAnswer(firstPicked)}>Done</button>
                        )}
                      </div>
                    )}
                    {/* The offer: yes please / not now. */}
                    {message.offerReal && awaitingOffer && (
                      <div className="demo-signup-button-container demo-gate-buttons">
                        <button className="demo-signup-button" onClick={() => handleOffer('yes')}>Yes please</button>
                        <button className="demo-signup-button demo-gate-demo-button" onClick={() => handleOffer('no')}>Not now</button>
                      </div>
                    )}
                    {/* Show create community button if this message has one */}
                    {message.showCreateCommunityButton && (
                      <div className="demo-signup-button-container">
                        <button
                          className="demo-signup-button demo-create-community-button"
                          onClick={() => handleCreateCommunityClick()}
                        >
                          Create Community
                        </button>
                      </div>
                    )}
                    {/* Demo vs sign-up gate shown with the intro message.
                        "Start Demo" is shown first (left), stays mounted once
                        clicked (label crossfades to "Demo Active"); "Sign Up Now"
                        is second (right) and fades away and unmounts. If "Sign Up
                        Now" is chosen instead, both disappear since the user is
                        navigating away. */}
                    {message.showDemoGateButtons && (awaitingDemoGate || demoStarted) && (
                      <div className="demo-signup-button-container demo-gate-buttons">
                        <button
                          className={`demo-signup-button demo-gate-demo-button ${demoStarted ? 'demo-gate-demo-button--active' : ''}`}
                          disabled={demoStarted}
                          onClick={() => handleDemoGateChoice('demo', message.communityId)}
                        >
                          <span className={`demo-gate-label ${demoTransitioning ? 'demo-gate-label--fading' : ''}`}>
                            {demoStarted ? 'Demo Active' : 'Start Demo'}
                          </span>
                        </button>
                        {!demoStarted && (
                          <button
                            className={`demo-signup-button ${demoTransitioning ? 'demo-gate-signup-fading' : ''}`}
                            disabled={demoTransitioning}
                            onClick={() => handleDemoGateChoice('signup', message.communityId)}
                          >
                            Sign Up Now
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Show profiles if this message has them - now outside message container */}
                {message.profiles && message.profiles.length > 0 && (
                  <div className="demo-cards-container">
                    <div className="demo-people-cards">
                      {message.profiles.map((person) => (
                        <div key={person.id} className="demo-person-card" onClick={() => handlePersonClick(person.name)}>
                          <div className="demo-person-avatar">
                            {person.avatar ? (
                              <img src={person.avatar} alt={person.name} />
                            ) : (
                              <div className={`demo-avatar-placeholder demo-avatar-${(person.name.charCodeAt(0) % 5) + 1}`}>
                                <span className="demo-avatar-initials">
                                  {person.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="demo-person-content">
                            <h4>{person.name}</h4>
                            {(person.age || person.gender) && (
                              <div className="demo-person-details">
                                {person.age && <span className="demo-person-age">{person.age}</span>}
                                {person.gender && <span className="demo-person-gender">{person.gender}</span>}
                              </div>
                            )}
                            <p>{person.bio}</p>
                            <button
                              className={`demo-connect-btn ${interestedPeople.has(person.id) ? 'demo-connect-btn--active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setInterestedPeople(prev => new Set(prev).add(person.id));
                                handleInterestedClick();
                              }}
                            >Interested in connecting</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show community cards if this message has them */}
                {message.showCommunityCards && (
                  <div className="demo-cards-container">
                    <h3>Here are some communities that might interest you:</h3>
                    <div className="demo-community-cards">
                      {(() => {
                        // Filter communities based on specific IDs if provided
                        const communitiesToShow = message.specificCommunityIds && message.specificCommunityIds.length > 0 
                          ? communities.filter(community => message.specificCommunityIds.includes(community._id))
                          : communities.slice(0, 3);
                        
                        return communitiesToShow.map((community) => (
                          <div key={community._id} className="demo-community-wrapper" onClick={() => handleCommunitySelect(community)}>
                            <CommunityDetails 
                              community={community} 
                              onTagClick={() => {}} 
                            />
                            <div className="demo-join-overlay">
                              <button className="demo-join-btn">Join Community</button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    
                  </div>
                )}

              </React.Fragment>
            ))}



              {/* Only while really waiting for the bot's reply. */}
              {isLoading && <TypingIndicator isBot communityName={selectedCommunity?.title || 'Interconnector'} />}
              
              {/* Auto-scroll anchor */}
              <div ref={messagesEndRef} />
              </div>
            </div>
            
            {/* Demo Input */}
            <div className="demo-chatbox-input">
              <DemoInput 
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demo;